import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import Button from '@components/forms/Button';
import { ReactComponent as CheckIcon } from '@assets/icons/check_icon.svg';
import { ReactComponent as AppLogo } from '@assets/images/dark_logo_24.svg';
import { LIST_LENDER_ORGANISATIONS, bulkRequestLenderBrokers, post } from '@services';
import { getUserAuth, needsLenderOnboarding } from '@utils/userUtils';

interface LenderItem {
  organisationId?: string;
  organisationName?: string;
  name?: string;
  isStub?: boolean;
}

const displayName = (l: LenderItem) => l.organisationName || l.name || 'Unknown Lender';

const LenderOnboarding = () => {
  const navigate = useNavigate();
  const [priority, setPriority] = useState<LenderItem[]>([]);
  const [prospects, setProspects] = useState<LenderItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // Self-guard: only eligible new brokers may see this screen.
  useEffect(() => {
    const auth = getUserAuth();
    if (!needsLenderOnboarding(auth?.lenders, auth?.lenderBrokers)) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await post(LIST_LENDER_ORGANISATIONS, {});
        const all: LenderItem[] = resp?.lenderOrganisations || [];
        setPriority(all.filter((o) => !o.isStub));
        setProspects(all.filter((o) => o.isStub));
      } catch (e) {
        console.error('Failed to load lenders:', e);
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const toggle = (id?: string) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await bulkRequestLenderBrokers(Array.from(selectedIds));
      navigate('/', { replace: true });
    } catch (e) {
      console.error('Failed to submit lender selections:', e);
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  const lenderPill = (l: LenderItem, featured = false) => {
    const id = l.organisationId;
    const isSelected = !!id && selectedIds.has(id);
    return (
      <button
        key={id}
        type="button"
        onClick={() => toggle(id)}
        className={clsx(
          'inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border transition-colors',
          isSelected
            ? 'bg-blue-50 border-secondary text-secondary font-semibold'
            : featured
              ? 'bg-white border-secondary/40 text-grey-800 hover:border-secondary hover:bg-blue-50/50'
              : 'bg-white border-grey-300 text-grey-700 hover:border-secondary hover:text-secondary'
        )}>
        <span
          className={clsx(
            'w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border',
            isSelected
              ? 'border-secondary bg-secondary'
              : featured
                ? 'bg-white border-secondary/40'
                : 'bg-white border-grey-300'
          )}>
          {isSelected && <CheckIcon className="w-2.5 h-2.5 text-white" />}
        </span>
        {displayName(l)}
      </button>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-grey-50">
      <header className="bg-white border-b border-grey-200 px-6 py-4">
        <AppLogo />
      </header>

      <main className="flex-1 flex justify-center px-4 py-10">
        <div className="w-full max-w-2xl flex flex-col gap-6">
          <div>
            <h1 className="text-2xl font-bold text-grey-900">Which lenders do you work with?</h1>
            <p className="text-sm text-grey-500 mt-1">
              Select the lenders you work with, and we&apos;ll help you get connected.
            </p>
          </div>

          {loadError && (
            <p className="text-sm text-error-light">
              We couldn&apos;t load lenders. Please refresh and try again.
            </p>
          )}

          {isLoading && <p className="text-sm text-grey-400">Loading lenders…</p>}

          {priority.length > 0 && (
            <section className="bg-white border border-grey-200 rounded-xl shadow-sm p-5 flex flex-col gap-3">
              <div className="flex items-center gap-1.5">
                <svg
                  className="w-4 h-4 text-secondary"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true">
                  <path d="M10 1.6l2.47 5.005 5.525.803-3.998 3.897.944 5.502L10 14.213l-4.941 2.594.944-5.502L2.005 7.408l5.525-.803L10 1.6z" />
                </svg>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-secondary">
                  Featured lenders
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {priority.map((l) => lenderPill(l, true))}
              </div>
            </section>
          )}

          {prospects.length > 0 && (
            <div className="flex flex-wrap gap-2 px-1">
              {prospects.map((l) => lenderPill(l, false))}
            </div>
          )}

          {submitError && (
            <p className="text-sm text-error-light">
              Something went wrong sending your request. Please try again.
            </p>
          )}

          <div className="sticky bottom-0 bg-grey-50 border-t border-grey-200 pt-4 pb-2 mt-2 flex items-center justify-between gap-4">
            <span className="text-sm">
              {selectedIds.size > 0 ? (
                <span className="text-grey-600">
                  <span className="font-semibold text-secondary">{selectedIds.size}</span> selected
                </span>
              ) : (
                <span className="text-grey-400">Select at least one to continue</span>
              )}
            </span>
            <Button
              className="bg-primary text-white normal-case font-semibold rounded-lg text-sm py-2.5 px-6 disabled:opacity-50"
              disabled={selectedIds.size === 0 || isSubmitting}
              onClick={handleSubmit}>
              {isSubmitting ? 'Submitting...' : 'Continue'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LenderOnboarding;
